import { createColumnHelper } from "@tanstack/react-table";
import { moneyDisplay } from "@icraftx/shared/v1";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import { FilterPartyName } from "@/modules/core/components/DueBadge";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import {
  togglePartyFilter,
  type PartyFilter,
} from "@/modules/core/list-cell-actions";
import { DeskDocListRowMenu } from "@/modules/shell/DeskDocListRowMenu";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import { stashListReturn } from "@/modules/shell/list-return-state";
import { relativeTimeLabel } from "@/modules/shell/relative-time";
import type { SalesInvoiceListRow } from "@/shared/api/sales";

const columnHelper =
  createColumnHelper<DeskTableFeatures, SalesInvoiceListRow>();

export type SalesInvoiceListCellActions = {
  listPath: string;
  partyFilter: PartyFilter | null;
  setPartyFilter: (next: PartyFilter | null) => void;
};

export function salesInvoiceColumns(
  companySlug: string,
  actions: SalesInvoiceListCellActions,
) {
  return columnHelper.columns([
    columnHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 120,
      cell: (info) => {
        const row = info.row.original;
        const docTo = `/c/${companySlug}/sales/invoices/${row.id}`;
        return (
          <DocNumberLink
            to={docTo}
            onClick={() => stashListReturn(actions.listPath, row.id)}
          >
            {info.getValue()}
          </DocNumberLink>
        );
      },
    }),
    columnHelper.accessor("customerName", {
      id: "customerName",
      header: "Customer",
      size: 180,
      cell: (info) => {
        const row = info.row.original;
        return (
          <FilterPartyName
            name={info.getValue()}
            onClick={() =>
              actions.setPartyFilter(
                togglePartyFilter(actions.partyFilter, {
                  id: row.customerId,
                  name: row.customerName,
                }),
              )
            }
          />
        );
      },
    }),
    columnHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/sales/invoices/${info.row.original.id}`}
        />
      ),
    }),
    columnHelper.accessor("docDate", {
      id: "docDate",
      header: "Date",
      size: 110,
      enableSorting: false,
      cell: (info) => info.getValue() || "—",
    }),
    columnHelper.accessor("paymentDueOn", {
      id: "paymentDueOn",
      header: "Due",
      size: 110,
      enableSorting: false,
      cell: (info) => info.getValue() || "—",
    }),
    columnHelper.accessor("grossTotal", {
      id: "grossTotal",
      header: "Total",
      size: 110,
      meta: { align: "right" },
      enableSorting: false,
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("updatedAt", {
      id: "updatedAt",
      header: "Updated",
      size: 88,
      enableSorting: false,
      cell: (info) => {
        const raw = info.getValue();
        return raw ? (
          <span className="text-xs text-[var(--color-muted)] tabular-nums">
            {relativeTimeLabel(raw)}
          </span>
        ) : (
          "—"
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 48,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const docPath = `/c/${companySlug}/sales/invoices/${row.original.id}`;
        return <DeskDocListRowMenu docPath={docPath} tabLinks={[]} />;
      },
    }),
  ]);
}
